<template>
  <div class="relative w-full" v-click-outside-input="closeList">
    <!-- Label -->
    <p class="flex justify-between mb-1">
      <span>{{ lableText }}</span>
    </p>

    <!-- Input Field + Icons -->
    <div class="relative">
      <input
        type="text"
        v-model="inputValue"
        :id="inputId"
        :maxlength="textMaxlength"
        :disabled="disabled"
        :readonly="setReadOnly"
        autocomplete="off"
        :autofocus="setAutoFocust"
        :class="['dropdown-input', borderClass, { 'border-error': showError }]"
        @focus="focusInput"
        @blur="blurInput"
        @input="handleInput"
        @click="toggleDropdown"
        @keydown="checkKey"
        @keypress="keyPressAction"
        @keypress.enter="enterkeyPressAction"
        @keyup="keyup"
        :placeholder="placholderText"
        :style="{ paddingRight: inputPaddingRight }"
      />

      <!-- Icons in sequence: Clear → Arrow → Add New -->
      <span
        v-if="showClearIcon && inputValue"
        @click="clearInput"
        class="absolute top-1/2 transform -translate-y-1/2 cursor-pointer"
        :style="{ right: showArrow && showPlus ? '80px' : showArrow || showPlus ? '50px' : '12px' }"
      >
        <img src="@/assets/images/svg/clear-input.svg" alt="clearInput" width="12" height="12">
      </span>

      <span
        v-if="showArrow"
        class="absolute top-1/2 transform -translate-y-1/2 pointer-events-none"
        :style="{ right: showPlus ? '40px' : '12px' }"
      >
        <i :class="opened ? 'fas fa-chevron-up text-gray-500' : 'fas fa-chevron-down text-gray-500'"></i>
      </span>

      <span
        v-if="showPlus"
        @click="addNew"
        class="absolute top-1/2 transform -translate-y-1/2 cursor-pointer"
        :style="{ right: '12px' }"
      >
        <i class="fas fa-plus text-primary"></i>
      </span>
    </div>

    <!-- Dropdown List -->
    <div
      v-show="opened"
      :id="listId"
      class="absolute z-10 w-full bg-white border border-gray-300 shadow-lg max-h-52 overflow-auto mt-1 rounded"
    >
      <div v-if="displayList.length">
        <div
          v-for="(item, index) in displayList"
          :key="index"
          @click.stop.prevent="selectItem(item)"
          :class="[
            'p-3 cursor-pointer hover:bg-gray-100 flex items-center',
            downPressCount === index ? 'bg-gray-200' : '',
            inputValue === item[keyName] ? 'bg-info text-white' : ''
          ]"
        >
          <span v-if="renderHtml" v-html="item[keyName]"></span>
          <span v-else>{{ item[keyName] }}</span>
        </div>
      </div>
      <div v-else class="p-2 text-gray-500 text-sm">
        {{ recordMessage || 'No Record Found' }}
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

Vue.directive('click-outside-input', {
  bind(el, binding, vnode) {
    el.clickOutsideEvent = function(event) {
      if (!(el === event.target || el.contains(event.target))) {
        vnode.context[binding.expression](event);
      }
    };
    document.body.addEventListener('click', el.clickOutsideEvent);
  },
  unbind(el) {
    document.body.removeEventListener('click', el.clickOutsideEvent);
  }
});

export default {
  props: [
    "listId", "keyName", "items", "inputext", "textMaxlength", "lableText", 
    "inputId", "autoFocus", "showArrow", "placholderText", "showClearIcon", 
    "showClearIconNear", "setReadOnly", "fieldError", "disabled", "renderHtml", 
    "borderType", "showPlus", "searchCallApi", "searchRecordMessage"
  ],
  data() {
    return {
      inputValue: '',
      displayList: [],
      opened: false,
      downPressCount: null,
      scrollCount: null,
      isFocused: false,
      showError: false,
      recordMessage: '',
      setAutoFocust: false,
    };
  },
  computed: {
    borderClass() {
      return this.borderType === 'bottom'
        ? 'border border-gray-300 focus:border-primary focus:ring-0'
        : 'border border-gray-300 rounded focus:border-primary focus:ring-1';
    },
    inputPaddingRight() {
      let padding = 12;
      if (this.showPlus) padding += 24;
      if (this.showArrow) padding += 24;
      if (this.showClearIcon && this.inputValue) padding += 24;
      return padding + 'px';
    }
  },
  watch: {
    fieldError(val) { this.showError = val; },
    inputext(val) { this.inputValue = val || ''; this.displayList = this.items; },
    items(val) { this.displayList = val; },
    searchRecordMessage(val) { this.recordMessage = val; }
  },
  mounted() {
    if (this.inputext) this.inputValue = this.inputext;
    if (this.autoFocus) this.$nextTick(() => document.getElementById(this.inputId)?.focus());
    this.displayList = this.items;
  },
  methods: {
    toggleDropdown() { this.opened = true; },
    focusInput() { this.isFocused = true; this.opened = true; },
    blurInput() { setTimeout(() => { this.opened = false; this.isFocused = false; }, 200); },
    handleInput() { this.$emit('inputChangeAction', this.inputValue); this.filterList(); },
    filterList() {
      if (!this.searchCallApi) {
        const filter = this.inputValue.toLowerCase();
        this.displayList = this.items.filter(item =>
          item[this.keyName].toLowerCase().includes(filter)
        );
      }
    },
    clearInput() { this.inputValue = ''; this.opened = true; this.$emit('clearInput'); },
    addNew() { this.$emit('addNew'); },
    selectItem(item) { 
      if (!item.isDisable) {
        this.inputValue = item[this.keyName]; 
        this.$emit('selectNewForVal', item, this.keyName); 
        this.opened = false;
      }
    },
    checkKey(e) {
      if (e.keyCode === 40) this.downArrow();
      if (e.keyCode === 38) this.upArrow();
      if (e.keyCode === 13 && this.downPressCount !== null) this.selectItem(this.displayList[this.downPressCount]);
    },
    downArrow() {
      if (this.downPressCount === null) this.downPressCount = 0;
      else if (this.downPressCount < this.displayList.length - 1) this.downPressCount++;
      this.scrollList();
    },
    upArrow() {
      if (this.downPressCount > 0) this.downPressCount--;
      this.scrollList();
    },
    scrollList() {
      const listEl = document.getElementById(this.listId);
      if (listEl) listEl.scrollTop = this.downPressCount * 52;
    },
    keyPressAction() { this.$emit('keyPressAction'); },
    enterkeyPressAction() { /* handled in checkKey */ },
    keyup() { this.$emit('keyup'); },
    closeList() { this.opened = false; }
  }
};
</script>

<style scoped>
.dropdown-input {
  width: 100%;
  padding: 12px 12px;
  font-size: 16px;
  outline: none;
  background-color: white;
}

.dropdown-input:focus {
  border-color: #1295BA;
}

.border-error {
  border-color: #ef4444 !important;
}
</style>
